import { useTranslation } from 'react-i18next';
import React from 'react';
import classNames from 'classnames';
import { Button, Collapse, Input } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import collect from 'collect.js';
import { CuisineCategories, CuisineData } from '../Constants';

/**
 * @param hideHeading  Suppress the inline heading + description. The onboarding
 *   wizard renders this inside a titled card section and would otherwise show
 *   the heading twice; the Add-Company modal keeps its own inline heading.
 */
export default function CuisineType({ data, onChange, className, hideHeading = false }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:Company:AddCompanyModal' });
  const [searchTerm, setSearchTerm] = React.useState('');

  const cuisine = collect(CuisineCategories)
    .values()
    .map((category) => ({
      key: category,
      label: t(`cuisine_categories.${category}`),
      children: CuisineData[category]
        .filter(
          (type) => searchTerm === '' || t(`cuisine_types.${type}`).toLowerCase().includes(searchTerm.toLowerCase()),
        )
        .map((type) => ({
          key: type,
          label: t(`cuisine_types.${type}`),
          value: type,
        })),
    }))
    .filter((category) => category.children.length > 0)
    .toArray();

  const handleCuisineTypeSelect = (type, checked) => {
    const currentTypes = data?.cuisine_types || [];
    let newTypes;

    if (checked) {
      newTypes = [...currentTypes, type];
    } else {
      newTypes = currentTypes.filter((t) => t !== type);
    }

    onChange('cuisine_types', newTypes);
  };

  return (
    <div className={className}>
      {!hideHeading && (
        <>
          <h3 className="mb-xs">{t('cuisine_type.title')}</h3>
          <p className="mb-md text-gray-600">{t('cuisine_type.description')}</p>
        </>
      )}
      <div className="mb-lg">
        <Input
          placeholder={t('cuisine_type.placeholder')}
          prefix={<span>🔍</span>}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
        />
      </div>
      {data?.cuisine_types?.length > 0 && (
        <div className="mb-lg">
          <div className="flex flex-wrap gap-2">
            {data.cuisine_types.map((type) => (
              <Button
                key={type}
                onClick={() => handleCuisineTypeSelect(type, false)}
                icon={<CloseOutlined />}
                type="dashed"
              >
                {t(`cuisine_types.${type}`)}
              </Button>
            ))}
          </div>
        </div>
      )}
      <Collapse
        accordion={false}
        items={cuisine.map((category) => ({
          key: category.key,
          label: <h6 className="font-medium text-sm">{category.label}</h6>,
          children: (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {category.children.map((type) => (
                <div
                  key={type.key}
                  className={classNames(
                    'py-2xs px-xs rounded-md border border-solid',
                    'cursor-pointer transition-colors flex items-center',
                    {
                      'bg-brand-primary text-white border-brand-primary': data?.cuisine_types?.includes(type.value),
                      'bg-gray-50 border-gray-200 hover:bg-gray-100': !data?.cuisine_types?.includes(type.value),
                    },
                  )}
                  onClick={() => handleCuisineTypeSelect(type.value, !data?.cuisine_types?.includes(type.value))}
                >
                  <h6 className="text-xs font-medium">{type.label}</h6>
                </div>
              ))}
            </div>
          ),
        }))}
      />
    </div>
  );
}
